import React, { createContext, Dispatch, useContext, useEffect, useReducer } from "react";
import { FreeplayEntry, FreeplayEvent, GetEventReturn } from "@freeplay/shared-code";
import googleFonts from "google-fonts";
import FontFaceObserver from "fontfaceobserver";

export enum Screen {
    NULL = "",
    HOME = "home",
    EVENT = "event",
    LEADERBOARD = "lb",
    MY_PICKS = "myPicks",
}

export type ApplicationContextProps = { appState: ApplicationState; appDispatch: Dispatch<ApplicationActions> };

export const ApplicationContext = createContext<ApplicationContextProps>({
    appState: {
        currentScreen: Screen.NULL,
        currentEventId: -1,
        activeEvents: [],
        playerEventHistory: [],
        key: null,
        splashTimerComplete: false,
        splashAnimateOutComplete: false,
        fontLoaded: false,
        menuVisible: false,
        headerVisible: false,
        bottomNavigationVisible: false,
        loginButtonVisible: false,
        loginButtonText: "",
        useFadeIn: true,
        reviewPicks: false,
        edgeDeploy: false,
        pageTitle: "",
        showLoadingAnimation: false,
        freeplayFocusedExperience: false,
        freeplayFocusedExperience404: false,
    },
    appDispatch: () => {
        // no op here
    },
});

type ApplicationProviderProps = {
    children?: React.ReactNode;
    eventId?: string;
};

export const LOCAL_STORAGE_KEY = "fpLoginState";

export const ApplicationProvider = ({ children, eventId }: ApplicationProviderProps): JSX.Element => {
    const [appState, appDispatch] = useReducer(applicationReducer, {
        currentScreen: Screen.NULL,
        currentEventId: -1,
        activeEvents: [],
        playerEventHistory: [],
        key: null,
        splashTimerComplete: false,
        splashAnimateOutComplete: false,
        fontLoaded: false,
        menuVisible: false,
        headerVisible: false,
        bottomNavigationVisible: false,
        loginButtonVisible: false,
        loginButtonText: "",
        useFadeIn: true,
        reviewPicks: false,
        edgeDeploy: false,
        pageTitle: "",
        showLoadingAnimation: false,
        freeplayFocusedExperience: eventId !== undefined,
        freeplayFocusedExperience404: false,
        focusedEventId: eventId,
    });

    // Font Loading
    useEffect(() => {
        (async () => {
            await Promise.all([
                loadGoogleFont("Bebas Neue"),
                loadGoogleFont("Open Sans", ["400", "600", "700", "800"]),
            ]);
            appDispatch({ type: "setFontLoaded" });
        })();

        setInterval(() => appDispatch({ type: "setSplashTimerComplete" }), 3000);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <ApplicationContext.Provider value={{ appState, appDispatch }}>{children}</ApplicationContext.Provider>;
};

export const useApplication = (): ApplicationContextProps => useContext(ApplicationContext);

export type ApplicationState = {
    fontLoaded: boolean;
    menuVisible: boolean;
    headerVisible: boolean;
    bottomNavigationVisible: boolean;
    currentScreen: string;
    activeEvents: FreeplayEvent[];
    playerEventHistory: FreeplayEntry[];
    latestEntry?: FreeplayEntry;
    eventReturn?: GetEventReturn;
    key: string | null;
    loginButtonVisible: boolean;
    loginButtonText: string;
    currentEventId: number;
    splashTimerComplete: boolean;
    splashAnimateOutComplete: boolean;
    useFadeIn: boolean;
    reviewPicks: boolean;
    edgeDeploy: boolean;
    pageTitle: string;
    showLoadingAnimation: boolean;
    lbSelectId?: number;
    freeplayFocusedExperience: boolean;
    freeplayFocusedExperience404: boolean;
    focusedEventId?: string;
};

export type ApplicationActions =
    | { type: "setFontLoaded" }
    | { type: "setActiveEvents"; events?: FreeplayEvent[] }
    | { type: "setPlayerEventHistory"; events?: FreeplayEntry[] }
    | { type: "setCurrentEvent"; eventId: number }
    | { type: "setMenuVisible"; menuVisible: boolean }
    | { type: "setHeaderVisible"; headerVisible: boolean }
    | { type: "setBottomNavigationVisible"; bottomNavigationVisible: boolean }
    | { type: "setLoginButtonVisible"; buttonVisible: boolean; buttonString: string }
    | { type: "setLatestEntry"; entry?: FreeplayEntry }
    | { type: "setSplashTimerComplete" }
    | { type: "setSplashAnimateOutComplete" }
    | { type: "setUseFadeIn"; useFadeIn: boolean }
    | { type: "setReviewPicks"; reviewPicks: boolean }
    | { type: "showHome" }
    | { type: "showLeaderboard"; eventId?: number }
    | { type: "showMyPicks" }
    | { type: "showEventView"; eventId: number } // 2.0
    | { type: "restoreSessionFromLogin"; previousState: string }
    | { type: "setPageTitle"; pageTitle: string }
    | { type: "setLoading"; loading: boolean }
    | { type: "playButtonPressed" }
    | { type: "setFreeplayFocusedExperience"; freeplayFocusedExperience: boolean }
    | { type: "setFreeplayFocusedExperience404"; freeplayFocusedExperience404: boolean };

export type AppContainer = {
    appDispatch: React.Dispatch<ApplicationActions>;
    appState: ApplicationState;
};

export const applicationReducer = (state: ApplicationState, action: ApplicationActions): ApplicationState => {
    switch (action.type) {
        case "setFontLoaded":
            return { ...state, fontLoaded: true };
        // CM: Note for now just disabling the menus entrirely for edge deploys, we may want to
        // look at better solutions if this doesn't cover some cases or is too hard to
        // manage
        // CM: Note me 15 minutes later, we're gonna want a better solution
        case "setMenuVisible":
            return { ...state, menuVisible: state.edgeDeploy ? false : action.menuVisible };
        case "setHeaderVisible":
            return { ...state, headerVisible: state.edgeDeploy ? false : action.headerVisible };
        case "setBottomNavigationVisible":
            return { ...state, bottomNavigationVisible: state.edgeDeploy ? false : action.bottomNavigationVisible };
        case "setActiveEvents":
            return { ...state, activeEvents: action.events ?? [] };
        case "setPlayerEventHistory":
            return { ...state, playerEventHistory: action.events ?? [] };
        case "setLatestEntry":
            return { ...state, latestEntry: action.entry };
        case "setSplashTimerComplete":
            return { ...state, splashTimerComplete: true };
        case "setSplashAnimateOutComplete":
            return { ...state, splashAnimateOutComplete: true };
        case "setUseFadeIn":
            return { ...state, useFadeIn: action.useFadeIn };
        case "setReviewPicks":
            return { ...state, reviewPicks: action.reviewPicks };
        case "setFreeplayFocusedExperience":
            return {
                ...state,
                freeplayFocusedExperience: action.freeplayFocusedExperience,
            };
        case "setFreeplayFocusedExperience404":
            return {
                ...state,
                freeplayFocusedExperience404: action.freeplayFocusedExperience404,
            };
        case "showEventView":
            return {
                ...state,
                currentScreen: Screen.EVENT,
                headerVisible: state.edgeDeploy ? false : true,
                bottomNavigationVisible: false,
                currentEventId: action.eventId ?? state.currentEventId,
            };
        case "playButtonPressed":
            return {
                ...state,
                currentScreen: state.focusedEventId ? Screen.EVENT : Screen.HOME,
                headerVisible: state.edgeDeploy ? false : true,
                bottomNavigationVisible: state.edgeDeploy || state.focusedEventId ? false : true,
                loginButtonVisible: false,
                pageTitle: "Home",
            };
        case "showHome":
            return {
                ...state,
                currentScreen: Screen.HOME,
                headerVisible: state.edgeDeploy ? false : true,
                bottomNavigationVisible: state.edgeDeploy ? false : true,
                loginButtonVisible: false,
                pageTitle: "Home",
            };
        case "showLeaderboard":
            return {
                ...state,
                currentScreen: Screen.LEADERBOARD,
                headerVisible: state.edgeDeploy ? false : true,
                bottomNavigationVisible: state.edgeDeploy ? false : true,
                lbSelectId: action.eventId,
                pageTitle: "Leaderboard",
            };
        case "showMyPicks":
            return {
                ...state,
                currentScreen: Screen.MY_PICKS,
                headerVisible: state.edgeDeploy ? false : true,
                bottomNavigationVisible: state.edgeDeploy ? false : true,
                pageTitle: "My Picks",
            };
        case "restoreSessionFromLogin": {
            // This will need guards and stuff to make sure nothing bad happens
            const previousState = JSON.parse(action.previousState) as ApplicationState;
            return {
                ...previousState,
                menuVisible: false,
                useFadeIn: true,
            };
        }
        case "setLoginButtonVisible":
            return { ...state, loginButtonVisible: action.buttonVisible, loginButtonText: action.buttonString };
        case "setCurrentEvent":
            return { ...state, currentEventId: action.eventId };
        case "setPageTitle":
            return { ...state, pageTitle: action.pageTitle };
        case "setLoading":
            return { ...state, showLoadingAnimation: action.loading };
    }
};

const loadGoogleFont = (fontName: string, weight: true | string[] = true) => {
    googleFonts.add({ [fontName]: weight });
    const fo = new FontFaceObserver(fontName);
    return fo.load().catch((err: Error) => console.log(fontName, err));
};
